import React, { useCallback, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, AttributionControl, useMap, Polyline } from "react-leaflet";
import { FullscreenControl } from "react-leaflet-fullscreen";
import "react-leaflet-fullscreen/styles.css";
import L, { LatLngTuple } from 'leaflet';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import { clearFilterSlice, setFilter, setFilterToApply } from '../../../../../../features/filter/filterSlice';
import { LayoutConfigEntity } from '../../../../../../app/enums/LayoutConfigEntity';
import { SelectModel } from '../../../../../../app/types/SelectModel';
import { GridFilter } from '../../../../../../app/types/GridFilter';
import { useDataHelperApi } from '../../../../../../app/api/dataHelper';
import { isAxiosError } from "axios";
import { toast } from "react-toastify";
import { IRegionViewModel } from '../../../../../../app/interfaces/shared/IRegionViewModel';
import { selectFilter, selectFilterToApply } from '../../../../../../features/filter/filterSliceSelectors';
import { useTranslation } from 'react-i18next';

type OfferMapContentProps = {
    gridData: any;
};

// Custom Control Component
const ViewModeControl = ({ viewMode, setViewMode }: { viewMode: 'C' | 'R' | 'P'; setViewMode: (mode: 'C' | 'R' | 'P') => void }) => {
    const map = useMap();  // Get the map instance

    const {t} = useTranslation();

    useEffect(() => {
        const control = new L.Control({ position: 'topright' }); // Create a Leaflet control

        control.onAdd = function (map: L.Map) {  // Use a standard function to access 'this'
            const div = L.DomUtil.create('div', 'leaflet-control-viewmode'); // Create a div for the control
            div.style.backgroundColor = 'white';
            div.style.padding = '10px';
            div.style.borderRadius = '5px';
            div.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)';
            div.style.display = "flex";

            const countriesLabel = L.DomUtil.create('label', 'leaflet-control-label', div); // Create a label for Countries
            countriesLabel.style.display = "flex";
            countriesLabel.style.marginRight = "15px";

            const countriesInput = L.DomUtil.create('input', 'leaflet-control-input', countriesLabel) as HTMLInputElement;
            countriesInput.type = 'radio';
            countriesInput.value = 'C';
            countriesInput.checked = viewMode === 'C';
            countriesInput.style.marginRight = '5px';
            countriesInput.addEventListener('change', () => setViewMode('C'));
            countriesLabel.prepend(countriesInput);
            countriesLabel.append(t("offer.mapView.country-label"))

            const regionsLabel = L.DomUtil.create('label', 'leaflet-control-label', div); // Create a label for Regions
            regionsLabel.style.display = "flex";
            regionsLabel.style.marginRight = "15px";
            const regionsInput = L.DomUtil.create('input', 'leaflet-control-input', regionsLabel) as HTMLInputElement;

            regionsInput.type = 'radio';
            regionsInput.value = 'R';
            regionsInput.checked = viewMode === 'R';
            regionsInput.style.marginRight = '5px';
            regionsInput.addEventListener('change', () => setViewMode('R'));
            regionsLabel.prepend(regionsInput);
            regionsLabel.append(t("offer.mapView.region-label"));

            const provincesLabel = L.DomUtil.create('label', 'leaflet-control-label', div); // Create a label for Provinces
            provincesLabel.style.display = "flex";
            const provincesInput = L.DomUtil.create('input', 'leaflet-control-input', provincesLabel) as HTMLInputElement;

            provincesInput.type = 'radio';
            provincesInput.value = 'P';
            provincesInput.checked = viewMode === 'P';
            provincesInput.style.marginRight = '5px';
            provincesInput.addEventListener('change', () => setViewMode('P'));
            provincesLabel.prepend(provincesInput);
            provincesLabel.append(t("offer.mapView.province-label"));

            L.DomEvent.disableClickPropagation(div); // Prevent map clicks when interacting with the control

            return div;
        };

        control.addTo(map);  // Add the control to the map

        return () => {
            map.removeControl(control); // Clean up when the component unmounts
        };
    }, [map, viewMode, setViewMode]); // Depend on map, viewMode, and setViewMode
    return null; // This component doesn't render anything directly
};


const OfferMapContent: React.FC<OfferMapContentProps> = ({ gridData }) => {

    console.log("gridData в карте:", gridData);

    const [countryMarkerClicked, setCountryMarkerClicked] = useState<string | null>(null);
    const [regionMarkerClicked, setRegionMarkerClicked] = useState<string | null>(null);
    const [provinceMarkerClicked, setProvinceMarkerClicked] = useState<string | null>(null);

    const defaultCenter: LatLngTuple = [50.450441, 30.523550]; // Явно указываем тип
    const [center, setCenter] = useState<LatLngTuple>(defaultCenter); // Явно указываем тип
    const [rerenderKey, setRerenderKey] = useState(0);

    const filter = useAppSelector(state =>
        selectFilter(state, LayoutConfigEntity.Offer));

    const filterToApply = useAppSelector(state =>
        selectFilterToApply(state, LayoutConfigEntity.Offer));

    //const [polylinePoints, setPolylinePoints] = React.useState<[[number, number], [number, number]] | null>(null);


    //баг: если 2 паза кликнуть на одну и ту же точку, то клик больше не работает 
    //это происходит из за dispatch(setFilter({ ...
    //временное решение: не применять фильтры при повторном клике на одну и ту же точку
    //здесь очищаем, чтобы при сбросе фильтра кликать можно было
    useEffect(() => {
        console.log("useEffect triggered");
        if (!gridData || gridData.length === 0 || !gridData[0]?.mapLines
            || (gridData[0]?.mapLines && gridData[0].mapLines.length === 0)) {
            console.log("Setting markerClicked to null inside useEffect");
            setCountryMarkerClicked(null);
            setRegionMarkerClicked(null);
            setProvinceMarkerClicked(null);
        }
    }, [gridData]); // Зависимость useEffect от gridData

    useEffect(() => {

        if (!gridData || gridData.length === 0 || !gridData[0]?.mapCenterCoordinates) {
        }
        else {
            const latString = gridData[0]?.mapCenterCoordinates.lat;
            const lonString = gridData[0]?.mapCenterCoordinates.lon;

            // Проверяем, что latString и lonString не null и не undefined
            if (latString && lonString) {
                console.log("change center called");
                const lat = parseFloat(latString.replace(',', '.'));
                const lon = parseFloat(lonString.replace(',', '.'));

                setRerenderKey(prevKey => prevKey + 1);
                setCenter([lat, lon]); // Обновляем состояние (перемещаем карту)

            }

        }

    }, [gridData]); // Зависимость useEffect от gridData

    const {
        getRegion: {
            query: getRegion
        }
    } = useDataHelperApi();

    const load = async (filter: string | null, type: string | null, country: string | null) => {
        try {
            const response = await getRegion(filter, type, country);

            if (response?.status === 200 && response.data) {
                return response.data;
            }
        } catch (e) {
            if (isAxiosError(e)) {
                toast.error(`Unable to get region. Error: ${e.message}`);
            }
        }

        return {} as IRegionViewModel;
    }


    const dispatch = useAppDispatch();

    // const {
    //     filterToApply,
    //     entity
    // } = useGridFilterPanelLayoutContext();

    const [viewMode, setViewMode] = useState<'C' | 'R' | 'P'>('C'); // 'C' for country, 'R' for region, 'P' for province

    const [pointType, setPointType] = useState<string | null>(null); // Состояние для хранения типа точки

    const createDivIcon = (number: number, type: string, country: string, region: string, view: string) => {
        const color = type === 'S' ? 'green' : 'blue';
        return L.divIcon({
            className: 'custom-marker',
            html: `<div class="offer-map-marker" data-country="${country}" data-type="${type}" data-region="${region}" data-view="${view}" style="background-color: ${color}; border-radius: 5px; width: 20px; height: 20px; display: flex; justify-content: center; align-items: center; color: white; font-weight: bold;">${number}</div>`,
            iconSize: [20, 20],
            iconAnchor: [15, 15], // Center the icon on the coordinate
            popupAnchor: [0, -15] // Adjust popup position
        });
    };

    const createDualDivIcon = (number1: number, number2: number, country: string, region: string, view: string) => {
        return L.divIcon({
            className: 'custom-marker',
            html: `<div style="display: flex"><div class="offer-map-marker" data-country="${country}" data-type="S" data-region="${region}" data-view="${view}" style="background-color: green; border-radius: 5px; width: 20px; height: 20px; display: flex; justify-content: center; align-items: center; color: white; font-weight: bold;">${number1}</div><div class="offer-map-marker" data-country="${country}" data-type="D" data-region="${region}" data-view="${view}" style="background-color: blue; border-radius: 5px; width: 20px; height: 20px; display: flex; justify-content: center; align-items: center; color: white; font-weight: bold;">${number2}</div></div>`,
            iconSize: [40, 20],
            iconAnchor: [15, 15], // Center the icon on the coordinate
            popupAnchor: [0, -15] // Adjust popup position
        });

    };

    const handleMouseOverMarker = () => {
        setTimeout(() => {
            const markers = document.querySelectorAll('.offer-map-marker');

            console.log('handleMouseOverMarker: ', markers);

            if (markers) {
                markers.forEach((marker) => {
                    // Проверяем, добавлен ли уже слушатель
                    //Бывают случаи когда два раза подряд выполняется перерисовка страницы и тогда слушатели добавлялись дважды
                    //что потом мешала корректной работе наведения
                    if (!marker.getAttribute('data-mouseover-added')) {
                        marker.addEventListener('mouseover', (event) => {
                            const element = event.target as HTMLElement;
                            const type = element.getAttribute('data-type');
                            const country = element.getAttribute('data-country');

                            console.log('hover: ', type, country);
                        });
                        // Устанавливаем атрибут, чтобы отметить, что слушатель добавлен
                        marker.setAttribute('data-mouseover-added', 'true');
                    }
                })

            }
        }, 500);
    }

    const handleMarkerClick = () => {
        setTimeout(() => {
            const markers = document.querySelectorAll('.offer-map-marker');

            console.log('handleMarkerClick: ', markers);

            if (markers) {
                markers.forEach((marker) => {
                    // Проверяем, добавлен ли уже слушатель
                    //Бывают случаи когда два раза подряд выполняется перерисовка страницы и тогда слушатели добавлялись дважды
                    //что потом мешала корректной работе клика
                    if (!marker.getAttribute('data-mouseclick-added')) {
                        marker.addEventListener('click', async (event) => {
                            const element = event.target as HTMLElement;
                            const type = element.getAttribute('data-type');
                            const country = element.getAttribute('data-country');
                            const region = element.getAttribute('data-region');
                            const viewMode = element.getAttribute('data-view');

                            if (viewMode === 'C') {
                                //баг: если 2 паза кликнуть на одну и ту же точку, то клик больше не работает 
                                //это происходит из за dispatch(setFilter({ ...
                                //временное решение: не применять фильтры при повторном клике на одну и ту же точку
                                console.log("countryMarkerClicked = " + countryMarkerClicked);
                                console.log("country = " + country);
                                if (countryMarkerClicked !== (country + "|" + type)) {

                                    //получаем данные по региону с апи
                                    //пока сделал только для стран, доделать для регионов
                                    const countryInfo = await load(country, "C", country);


                                    console.log("api data: ");
                                    console.log(countryInfo);

                                    const hasValidFields = (
                                        countryInfo &&
                                        countryInfo.alias &&
                                        countryInfo.alias.trim() !== "" &&
                                        countryInfo.value &&
                                        countryInfo.value.trim() !== "" &&
                                        countryInfo.description2 &&
                                        countryInfo.description2.trim() !== ""
                                    );

                                    if (hasValidFields) {
                                        //подготовалвиаем данные для фильтра 

                                        const filterData: Array<SelectModel> = [
                                            // { text: "PL",
                                            //   value: "2326",
                                            //     description2: "PL" 
                                            // }

                                            {
                                                text: countryInfo?.alias || "",
                                                value: countryInfo?.value || "",
                                                description2: countryInfo?.description2 || ""
                                            }
                                        ];

                                        //при таком подходе стираются все старые фильтры и применяются новые 
                                        // const gridFilter: GridFilter = {
                                        //     invoiceNumber: null,
                                        //     quoteNumber: null,
                                        //     shipmentNumber: null,
                                        //     orderReleaseNumber: null,
                                        //     jobNumber: null,
                                        //     customerRefNumber: null,
                                        //     licencePlate: null,
                                        //     pickupDateFrom: null,
                                        //     pickupDateTo: null,
                                        //     deliveryDateFrom: null,
                                        //     deliveryDateTo: null,
                                        //     status: [],
                                        //     blockStatus: [],
                                        //     destination: type === "D" ? filterData : [],
                                        //     source: type === "S" ? filterData : [],
                                        //     supplier: [],
                                        //     equipment: [],
                                        //     serviceProvider: [],
                                        //     tags: [],
                                        //     uid: null, // Leave as null initially, will be overwritten in dispatch
                                        //     asstraIntegrationConstant: null,
                                        //     asstraIntegrationTemporary: null,
                                        //     responsibleVisibility: [],
                                        //     jobOrderRelease: null,
                                        //     curator: [],
                                        // };
                                        // //применяем фильтры
                                        // dispatch(setFilter({
                                        //     entity: LayoutConfigEntity.Offer,
                                        //     data: { ...gridFilter, uid: new Date().getTime() + "" }
                                        // }));

                                        // dispatch(setFilterToApply({
                                        //     entity: LayoutConfigEntity.Offer,
                                        //     data: { ...gridFilter, uid: new Date().getTime() + "" }
                                        // }));

                                        const updatedFilter = {
                                            ...filter,
                                            source: type === "S" ? filterData : [],
                                            destination: type === "D" ? filterData : []
                                        };
                                        const updatedFilterToApply = {
                                            ...filterToApply,
                                            source: type === "S" ? filterData : [],
                                            destination: type === "D" ? filterData : []
                                        };
                                        //применяем фильтры
                                        dispatch(setFilter({
                                            entity: LayoutConfigEntity.Offer,
                                            data: updatedFilter
                                        }));

                                        dispatch(setFilterToApply({
                                            entity: LayoutConfigEntity.Offer,
                                            data: updatedFilterToApply
                                        }));


                                        setCountryMarkerClicked(country + "|" + type);
                                        setRegionMarkerClicked(null);
                                        setProvinceMarkerClicked(null);
                                    }
                                }


                            }

                            if (viewMode === 'R') {
                                //баг: если 2 паза кликнуть на одну и ту же точку, то клик больше не работает 
                                //это происходит из за dispatch(setFilter({ ...
                                //временное решение: не применять фильтры при повторном клике на одну и ту же точку
                                console.log("regionMarkerClicked = " + regionMarkerClicked);
                                console.log("region = " + region);
                                if (regionMarkerClicked !== (region + "|" + type)) {

                                    //получаем данные по региону с апи
                                    //пока сделал только для стран, доделать для регионов
                                    const regionInfo = await load(region, "R", country);

                                    console.log("api data: ");
                                    console.log(regionInfo);

                                    const hasValidFields = (
                                        regionInfo &&
                                        regionInfo.alias &&
                                        regionInfo.alias.trim() !== "" &&
                                        regionInfo.value &&
                                        regionInfo.value.trim() !== "" &&
                                        regionInfo.description2 &&
                                        regionInfo.description2.trim() !== ""
                                    );

                                    if (hasValidFields) {
                                        //подготовалвиаем данные для фильтра 

                                        const filterData: Array<SelectModel> = [
                                            // { text: "PL",
                                            //   value: "2326",
                                            //     description2: "PL" 
                                            // }

                                            {
                                                text: regionInfo?.alias || "",
                                                value: regionInfo?.value || "",
                                                description2: regionInfo?.description2 || ""
                                            }
                                        ];

                                        //при таком подходе стираются все старые фильтры и применяются новые 
                                        // const gridFilter: GridFilter = {
                                        //     invoiceNumber: null,
                                        //     quoteNumber: null,
                                        //     shipmentNumber: null,
                                        //     orderReleaseNumber: null,
                                        //     jobNumber: null,
                                        //     customerRefNumber: null,
                                        //     licencePlate: null,
                                        //     pickupDateFrom: null,
                                        //     pickupDateTo: null,
                                        //     deliveryDateFrom: null,
                                        //     deliveryDateTo: null,
                                        //     status: [],
                                        //     blockStatus: [],
                                        //     destination: type === "D" ? filterData : [],
                                        //     source: type === "S" ? filterData : [],
                                        //     supplier: [],
                                        //     equipment: [],
                                        //     serviceProvider: [],
                                        //     tags: [],
                                        //     uid: null, // Leave as null initially, will be overwritten in dispatch
                                        //     asstraIntegrationConstant: null,
                                        //     asstraIntegrationTemporary: null,
                                        //     responsibleVisibility: [],
                                        //     jobOrderRelease: null,
                                        //     curator: [],
                                        // };
                                        // //применяем фильтры
                                        // dispatch(setFilter({
                                        //     entity: LayoutConfigEntity.Offer,
                                        //     data: { ...gridFilter, uid: new Date().getTime() + "" }
                                        // }));

                                        // dispatch(setFilterToApply({
                                        //     entity: LayoutConfigEntity.Offer,
                                        //     data: { ...gridFilter, uid: new Date().getTime() + "" }
                                        // }));


                                        const updatedFilter = {
                                            ...filter,
                                            source: type === "S" ? filterData : [],
                                            destination: type === "D" ? filterData : []
                                        };
                                        const updatedFilterToApply = {
                                            ...filterToApply,
                                            source: type === "S" ? filterData : [],
                                            destination: type === "D" ? filterData : []
                                        };
                                        //применяем фильтры
                                        dispatch(setFilter({
                                            entity: LayoutConfigEntity.Offer,
                                            data: updatedFilter
                                        }));

                                        dispatch(setFilterToApply({
                                            entity: LayoutConfigEntity.Offer,
                                            data: updatedFilterToApply
                                        }));
                                        setRegionMarkerClicked(region + "|" + type);
                                        setCountryMarkerClicked(null);
                                        setProvinceMarkerClicked(null);
                                    }

                                }

                            }

                            if (viewMode === 'P') {
                                //баг: если 2 паза кликнуть на одну и ту же точку, то клик больше не работает 
                                //это происходит из за dispatch(setFilter({ ...
                                //временное решение: не применять фильтры при повторном клике на одну и ту же точку
                                console.log("provinceMarkerClicked = " + provinceMarkerClicked);
                                console.log("region = " + region);
                                if (provinceMarkerClicked !== (region + "|" + type)) {

                                    //получаем данные по региону с апи
                                    //пока сделал только для стран, доделать для регионов
                                    const provinceInfo = await load(region, "P", country);

                                    console.log("api data province: ");
                                    console.log(provinceInfo);

                                    const hasValidFields = (
                                        provinceInfo &&
                                        provinceInfo.alias &&
                                        provinceInfo.alias.trim() !== "" &&
                                        provinceInfo.value &&
                                        provinceInfo.value.trim() !== "" &&
                                        provinceInfo.description2 &&
                                        provinceInfo.description2.trim() !== ""
                                    );

                                    if (hasValidFields) {
                                        //подготовалвиаем данные для фильтра 

                                        const filterData: Array<SelectModel> = [
                                            // { text: "PL",
                                            //   value: "2326",
                                            //     description2: "PL" 
                                            // }

                                            {
                                                text: provinceInfo?.alias || "",
                                                value: provinceInfo?.value || "",
                                                description2: provinceInfo?.description2 || ""
                                            }
                                        ];

                                        //при таком подходе стираются все старые фильтры и применяются новые 
                                        // const gridFilter: GridFilter = {
                                        //     invoiceNumber: null,
                                        //     quoteNumber: null,
                                        //     shipmentNumber: null,
                                        //     orderReleaseNumber: null,
                                        //     jobNumber: null,
                                        //     customerRefNumber: null,
                                        //     licencePlate: null,
                                        //     pickupDateFrom: null,
                                        //     pickupDateTo: null,
                                        //     deliveryDateFrom: null,
                                        //     deliveryDateTo: null,
                                        //     status: [],
                                        //     blockStatus: [],
                                        //     destination: type === "D" ? filterData : [],
                                        //     source: type === "S" ? filterData : [],
                                        //     supplier: [],
                                        //     equipment: [],
                                        //     serviceProvider: [],
                                        //     tags: [],
                                        //     uid: null, // Leave as null initially, will be overwritten in dispatch
                                        //     asstraIntegrationConstant: null,
                                        //     asstraIntegrationTemporary: null,
                                        //     responsibleVisibility: [],
                                        //     jobOrderRelease: null,
                                        //     curator: [],
                                        // };
                                        // //применяем фильтры
                                        // dispatch(setFilter({
                                        //     entity: LayoutConfigEntity.Offer,
                                        //     data: { ...gridFilter, uid: new Date().getTime() + "" }
                                        // }));

                                        // dispatch(setFilterToApply({
                                        //     entity: LayoutConfigEntity.Offer,
                                        //     data: { ...gridFilter, uid: new Date().getTime() + "" }
                                        // }));


                                        const updatedFilter = {
                                            ...filter,
                                            source: type === "S" ? filterData : [],
                                            destination: type === "D" ? filterData : []
                                        };
                                        const updatedFilterToApply = {
                                            ...filterToApply,
                                            source: type === "S" ? filterData : [],
                                            destination: type === "D" ? filterData : []
                                        };
                                        //применяем фильтры
                                        dispatch(setFilter({
                                            entity: LayoutConfigEntity.Offer,
                                            data: updatedFilter
                                        }));

                                        dispatch(setFilterToApply({
                                            entity: LayoutConfigEntity.Offer,
                                            data: updatedFilterToApply
                                        }));

                                        setProvinceMarkerClicked(region + "|" + type);
                                        setCountryMarkerClicked(null);
                                        setRegionMarkerClicked(null);
                                    }

                                }

                            }



                            //                                 // Define the coordinates for Minsk and Warsaw
                            // const minsk: [number, number] = [52.22977, 21.01178]; // Minsk coordinates (latitude, longitude)
                            // const warsaw: [number, number] = [45.76601, 4.8795]; // Warsaw coordinates (latitude, longitude)

                            // setPolylinePoints([minsk, warsaw]);
                            // console.log("Drawing polyline between Minsk and Warsaw");

                            console.log('click: ', type, country);
                        });


                        // Устанавливаем атрибут, чтобы отметить, что слушатель добавлен
                        marker.setAttribute('data-mouseclick-added', 'true');
                    }
                })
            }
        }, 500);
    }


    const renderMarkers = () => {
        if (!gridData || gridData.length === 0 || !gridData[0]?.mapCoordinates) {
            return null;
        }

        console.log('renderMarkersBegin');

        const markers = gridData[0].mapCoordinates.map((coordinate: any, index: number) => {
            const lat = parseFloat(coordinate.lat.replace(',', '.'));
            const lon = parseFloat(coordinate.lon.replace(',', '.'));

            if (isNaN(lat) || isNaN(lon)) {
                console.warn(`Invalid coordinates at index ${index}: lat=${coordinate.lat}, lon=${coordinate.lon}`);
                return null;
            }

            if (coordinate.viewMode !== viewMode) return null; // Filter based on viewMode


            let icon;
            if (coordinate.typeS === true && coordinate.typeD === true) {
                icon = createDualDivIcon(coordinate.countS, coordinate.countD, coordinate.country, coordinate.region, coordinate.viewMode);
            }
            else {
                const type = coordinate.typeS === true ? 'S' : 'D';
                const count = coordinate.typeS === true ? coordinate.countS : coordinate.countD;
                icon = createDivIcon(count, type, coordinate.country, coordinate.region, coordinate.viewMode);

            }


            return (
                <Marker
                    key={index}
                    position={[lat, lon]}
                    icon={icon}
                    eventHandlers={{
                        //click: (event) => { handleMarkerClick(event, coordinate.country); }
                    }}
                >
                    <Popup>
                        Type: {(coordinate.typeS && coordinate.typeD)
                            ? "S/D"
                            : coordinate.typeS
                                ? "Source"
                                : "Destination"} <br />
                        Country: {coordinate.country} <br />
                        {coordinate.typeS && coordinate.typeD ? (
                            <>
                                Number S: {coordinate.countS} <br />
                                Number D: {coordinate.countD} <br />
                            </>
                        ) : (
                            <>
                                Number: {coordinate.typeS ? coordinate.countS : coordinate.countD} <br />
                            </>
                        )}
                        Region: {coordinate.region}
                    </Popup>


                </Marker>
            );
        });

        console.log('renderMarkersEnd');

        return markers;
    };


    const renderLines = () => {

        console.log("render lines");

        if (!gridData || gridData.length === 0 || !gridData[0]?.mapLines) {
            return null;
        }

        return gridData[0].mapLines.map((line: any, index: number) => {
            const sourceLat = parseFloat(line.sourceLat.replace(',', '.'));
            const sourceLon = parseFloat(line.sourceLon.replace(',', '.'));
            const destLat = parseFloat(line.destLat.replace(',', '.'));
            const destLon = parseFloat(line.destLon.replace(',', '.'));

            if (isNaN(sourceLat) || isNaN(sourceLon) || isNaN(destLat) || isNaN(destLon)) {
                //console.warn(`Invalid coordinates at index ${index}: lat=${coordinate.lat}, lon=${coordinate.lon}`);
                return null;
            }

            if (line.viewMode !== viewMode) return null; // Filter based on viewMode

            const positions: [[number, number], [number, number]] = [
                [sourceLat, sourceLon],
                [destLat, destLon],
            ];

            // if (coordinate.viewMode !== viewMode) return null; // Filter based on viewMode  //This does not exist anymore.

            return (
                <Polyline
                    key={index} // Important: Add a unique key for each Polyline
                    positions={positions}
                    color="red" // Customize the line color
                    weight={2}   // Customize the line thickness
                    opacity={0.7}  // Customize the line opacity
                // Add more Leaflet Polyline options here as needed
                />
            );
        });
    };

    return (
        <MapContainer key={rerenderKey} center={center} zoom={4} scrollWheelZoom={true} attributionControl={false}>
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.dc.asstra.com/osm/{z}/{x}/{y}.png"
            />
            <FullscreenControl forceSeparateButton={true} position="topleft" />
            <AttributionControl prefix='<a href="https://leafletjs.com/">Leaflet</a>' />
            {/* {polylinePoints && <Polyline positions={polylinePoints} color="red" />} */}
            {renderLines()}
            <ViewModeControl viewMode={viewMode} setViewMode={setViewMode} />
            {renderMarkers()}
            {/*в handleMouseOverMarker и handleMarkerClick установлен таймеаут 500, так как
            были случаи, когда эти функции срабатывали, а маркеры еще не отрисовались
            из за этого слушатели событий не навешивались*/}
            {handleMouseOverMarker()}
            {handleMarkerClick()}
        </MapContainer>
    );
};

export default OfferMapContent;
